import React from "react";
import { Button, Form, Icon, Label, Modal } from "semantic-ui-react";
import { useTextResources } from "../../language";
import PropTypes from "prop-types";
import WeekDaysSelect from "../shared/WeekDaysSelect";
import { DatePicker } from "../shared/DatePicker";
import { useMemo } from "react";
import { useState } from "react";
import DropDown from "../shared/dropdown";
import RatesDropDown from "../shared/RatesDropDown"
import NumericInput from "../shared/NumericInput";
import { dateFormat, DiscountTypesEnum, RateCategory, RateDiscountTypesEnum, DiscountApplicationTypesEnum, Designations, DiscountPropertiesEnum } from "../shared/constants";
import { useEffect } from "react";
import { onError, onSuccess, onWarning } from "../shared/messages";
import { Rates } from "../../api/services/next";
import { makeDaysOfWeekArrayIntoInt } from "../shared/utils";
import moment from "moment";
import CheckBoxToggle from "../shared/CheckBoxToggle";
import HotelsDropDown from '../shared/HotelsDropDown';
import { useLoader } from "../../hooks/useLoader";

const DiscountPopup = ({ controller, startOpen, onClose, onSave, hotelId, ...rest }) => {
  const text = useTextResources();
  const loader = useLoader();
  const [open, setOpen] = useState(startOpen);
  const [dateRanges, setDateRanges] = useState([]);
  const [startDate, setStartDate] = useState(moment().startOf("day").toDate());
  const [endDate, setEndDate] = useState(moment().add(1, "day").endOf("day").subtract(10, "seconds").toDate());
  const [Weekdays, setWeekdays] = useState([]);
  const [discount, setDiscount] = useState({});
  const [rates, setRates] = useState([]);
  const [disocuntType, setDiscountType] = useState(1);
  const [selectedRateIds, setSelectedRateIds] = useState([]);
  const [priceIncTax, setPriceIncTax] = useState(false);
  const [selectedHotel, setSelectedHotel] = useState(hotelId);

  const setDates = (newDates, fromWhere) => {
    if (newDates[0] > newDates[1]) {
      setStartDate(newDates[fromWhere]);
      setEndDate(newDates[fromWhere]);
    }
    else {
      setStartDate(newDates[0])
      setEndDate(newDates[1])
    }
  }

  const resetFields = () => {
    setDateRanges([]);
    setStartDate(moment().startOf("day").toDate());
    setEndDate(moment().add(1, "day").endOf("day").subtract(10, "seconds").toDate());
    setWeekdays([]);
    setDiscount({});
    setDiscountType(1);
    setPriceIncTax(false);
    setSelectedRateIds([]);
  }

  useEffect(() => {
    if (controller) {
      window[controller] = (action) => {
        if (action === "open") {
          setOpen(true);
        }
      }
    }
    (async () => {
      try {
        let currentHotelRates = await Rates.GetRates({
          HotelId: selectedHotel,
          Includes: ["RateDiscounts"]
        });
        let filteredRates = currentHotelRates.filter(r => {
          return r.BaseRateId &&
            r.RateCategoryId !== RateCategory.DerivedPromotion &&
            !r.RateDiscounts?.some(x => x.RateDiscountTypeId !== RateDiscountTypesEnum.OverallDiscount);
        });
        let RateDiscountAmounts = await Rates.GetRateDiscountAmounts({ RateDiscountIds: filteredRates.reduce((arr, rate) => [...arr, ...(rate.RateDiscounts?.map(x => x.Id) || [])], []) })
        setRates(filteredRates.map(rate => {
          let rateDiscountIds = rate.RateDiscounts.map(x => x.Id);
          return { ...rate, RateDiscountAmounts: RateDiscountAmounts.filter(x => rateDiscountIds.includes(x.RateDiscountId)) }
        }));
      } catch (err) {
        onError(err);
      }
    })()
  },[selectedHotel]);

  const saveAction = () => {
    const missingFields = getMissingFields();
    if (missingFields.length === 0) {
      (async () => {
        try {
          loader(true);
          await Rates.SaveRateDiscounts(dateRanges.map(dateRange => selectedRateIds.map(rateId => {
            return {
              ...dateRange,
              Id: 0,
              RateDiscountTypeId: RateDiscountTypesEnum.OverallDiscount,
              DiscountApplicationTypeId: DiscountApplicationTypesEnum.Product,
              DiscountPercent: 0,
              PriceIncludesTax: priceIncTax,
              RateDiscountAmounts: Object.values(Designations).map(designationId => ({
                RateDiscountId: 0,
                DesignationId: designationId,
                DiscountPercent: discount.DiscountPercent,
                [priceIncTax ?
                  DiscountPropertiesEnum.DiscountAmountAfterTax :
                  DiscountPropertiesEnum.DiscountAmountBeforeTax]: discount.DiscountAmount,
                DiscountAmount: discount.DiscountAmount
              })),
              ...discount,
              Date: new Date(),
              RateId: rateId,
				IsActive: true,
            }

          })).flat());
          onSuccess();
          if (onSave) {
            onSave();
          }
          closeAction();
        } catch (error) {
          error.status === 400 ? onWarning(error) : onError(error);
        } finally {
          loader(false);
        }
      })();
    } else {
      onWarning(text("MissingFields").replace('{0}', missingFields.join(", ")));
    }
  }

  const getMissingFields = () => {
    let missingFields = [];
    if (dateRanges.length === 0)
      missingFields.push(text("DatesRange"));
    if (selectedRateIds.length === 0)
      missingFields.push(text("Rates"));
    if (discount.IsOverride === undefined)
      missingFields.push(text("ApplyAs"));
    if (!discount.DiscountPercent && !discount.DiscountAmount)
      missingFields.push(text("Discount"));
    if(!selectedHotel){
        missingFields.push(text("Hotel"));
    }
    return missingFields;
  }

  const closeAction = () => {
    setOpen(false);
    resetFields();
    onClose?.();
  }

  useEffect(() => {
    if (!open) {
      resetFields();
    }
  }, [open]);

  const addDateToList = () => {
    if (dateRanges.some((d) => moment(d.StartDate).isSame(startDate, "day") && moment(d.EndDate).isSame(endDate, "day"))) {
      onWarning(text("WarningDuplicateDates"));
      return;
    }
    setDateRanges([...dateRanges, {
      StartDate: startDate,
      EndDate: endDate,
      Weekdays: makeDaysOfWeekArrayIntoInt(Weekdays)
    }]);
  };

  const memoDatesForWeekComponent = useMemo(() => startDate && endDate ? [{ startDate, endDate }] : [], [startDate, endDate]);

  const discountTypes = [
    { Name: text("ByAmount"), Id: 1 },
    { Name: text("ByPercent"), Id: 2 },
  ]

  const changeTypes = [
    { Name: text("AddToExisting"), Value: false },
    { Name: text("OverrideExisting"), Value: true },
  ]

  return (
    <Modal size="small" open={open} dimmer centered {...rest}>
      <Modal.Header>{text("OverallDiscount")}</Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Group unstackable widths={"equal"} style={{ flexWrap: "wrap" }}>
            {dateRanges.map((dateRange, index) => (
              <Label.Group key={index}>
                <Label as="a">
                  {moment(dateRange.StartDate).format(dateFormat)}-{moment(dateRange.EndDate).format(dateFormat)}
                  <Icon
                    key={index}
                    name={"delete"}
                    onClick={() => setDateRanges(dateRanges.filter((date, dateIndex) => dateIndex !== index))}
                  />
                </Label>
              </Label.Group>
            ))}
          </Form.Group>
          <Form.Group widths={2}>
            <DatePicker label={text("From")} onChange={(value) => setDates([value, endDate], 0)} clearable={false} value={startDate} />
            <DatePicker label={text("To")} onChange={(value) => setDates([startDate, value], 1)} clearable={false} value={endDate} />
          </Form.Group>
          <WeekDaysSelect onChange={setWeekdays} dates={memoDatesForWeekComponent} />
          <Button Button size="small" primary onClick={addDateToList}>
            {text("Add")}
          </Button>
          <Form.Field label={text("Hotel")} required control={HotelsDropDown} onChange={setSelectedHotel} value={selectedHotel}/>
          <Form.Group widths={2}>
            <Form.Field
              label={text("RateDiscountType")}
              control={DropDown}
              textField="Name"
              valueField="Id"
              data={discountTypes}
              onChange={(value) => {
                setDiscountType(value);
                setDiscount({ ...discount, [value === DiscountTypesEnum.Amount ? "DiscountPercent" : "DiscountAmount"]: undefined });
              }}
              value={disocuntType}
            />
            <Form.Field
              data={rates
                .filter(rate =>
                  !rate.RateDiscountAmounts?.some(rda =>
                    disocuntType === DiscountTypesEnum.Amount ?
                      rda.DiscountPercent :
                      rda.DiscountAmountBeforeTax || rda.DiscountAmountAfterTax
                  ))
              }
              label={text("Rates")}
              multiple
              textField="Name"
              valueField="Id"
              control={RatesDropDown}
              onChange={setSelectedRateIds}
              value={selectedRateIds}
            />
          </Form.Group>
          <Form.Group widths={2}>
            <Form.Field label={text("ApplyAs")} control={DropDown} textField="Name" valueField="Value" data={changeTypes} onChange={(value) => setDiscount({ ...discount, IsOverride: value })} value={discount?.IsOverride} />
            <Form.Field label={text("Discount")} control={NumericInput} buttonPlacement="right" onChange={(value) => setDiscount({ ...discount, [disocuntType === DiscountTypesEnum.Percent ? "DiscountPercent" : "DiscountAmount"]: value })} />
          </Form.Group>
          <Form.Field
            label={text("Priceinctax")}
            control={CheckBoxToggle}
            onClick={() => {
              setPriceIncTax(!priceIncTax) ;
              setDiscount({ ...discount, PriceIncludesTax: !priceIncTax });
            }}
            checked={priceIncTax}
          />
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button negative onClick={closeAction}>
          {text("Close")}
        </Button>
        <Button positive onClick={saveAction}>
          {text("Save")}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

DiscountPopup.propTypes = {
  controller: PropTypes.object,
  startOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  rest: PropTypes.object,
  hotelId: PropTypes.number,
};

export default DiscountPopup;
